import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import sortBy from "lodash/sortBy";
import { useSnackbar } from "notistack";
import type { MediaTag } from "./mediaTagsApi";
import { addMediaTag, deleteMediaTag, listMediaTags, updateMediaTag } from "./mediaTagsApi";

const mediaTagsKey = "media-tags-dictionary";

export const useMediaTags = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: [mediaTagsKey],
    queryFn: ({ signal }) => listMediaTags(signal),
  });

  const addMutation = useMutation<MediaTag, unknown, { readonly name: string; readonly isPopular: boolean }>({
    mutationFn: ({ name, isPopular }) => addMediaTag(name, isPopular),
    onSuccess: async (data) => {
      enqueueSnackbar(`Tag '${data.name}' został dodany do słownika`, { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: [mediaTagsKey] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się dodać taga do słownika. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const updateMutation = useMutation<
    MediaTag,
    unknown,
    { readonly id: string; readonly name: string; readonly isPopular: boolean }
  >({
    mutationFn: ({ id, name, isPopular }) => updateMediaTag(id, name, isPopular),
    onSuccess: async (data) => {
      enqueueSnackbar(`Nazwa taga została zmieniona na ${data.name}`, { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: [mediaTagsKey] });
      await queryClient.invalidateQueries({ queryKey: ["media", "images"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zmienić nazwy taga. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const deleteMutation = useMutation<void, unknown, string>({
    mutationFn: (id) => deleteMediaTag(id),
    onSuccess: async () => {
      enqueueSnackbar("Tag został usunięty ze słownika", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: [mediaTagsKey] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć taga ze słownika. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  return {
    tags: sortBy(data ?? [], ({ name }) => name.trim().toLowerCase()),
    isLoading,
    addTag: (name: string, isPopular: boolean) => addMutation.mutateAsync({ name, isPopular }),
    updateTag: (id: string, name: string, isPopular: boolean) => updateMutation.mutateAsync({ id, name, isPopular }),
    deleteTag: (id: string) => deleteMutation.mutateAsync(id),
  };
};
